import { Switch } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDP_VALUES, PRICE_LOGIC, SellingModes } from '../../../../helper/constants';
import { mergedProduct, PriceRange, SkuAttribute, Variation } from '../../../../types/product';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';
import ContactSupplierSection from './ContactSupplierSection';
import PriceRangeDisplay from './PriceRangeDisplay';
import DeliveryDateBadge from './DeliveryDateBadge';
import { ProductDetailRightSkeleton } from './ProductDetailSkeletons';
import SupplierInfoSection from './SupplierInfoSection';
import { prepareDescriptionHtml, extractImagesFromDescription, appendSizeToImageUrl } from './utils';
import VariantList from './VariantList';
import SubscribeButton from './SubscribeButton';
import { FaWhatsapp, FaPhoneAlt } from 'react-icons/fa';
import { ProductsApiService } from '../../../../service/website/productsApiService';

interface ProductDetailRightProps {
  id: string;
  product: mergedProduct | any; // will remove this any afterwards.
  isDbProduct?: boolean;
  isFromMongoDB?: boolean;
  priceRanges: PriceRange[];
  setPreferredImage: (image: string) => void;
  selectedVariants: Record<string, any>;
  setSelectedVariants: (variants: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)) => void;
  pricingMode: string;
  setPricingMode: (mode: string) => void;
  onQuantityChangeForVariant: (skuId: string | number, quantity: number, variant: Variation) => void;
  variantPrices?: Record<string, any>;
  onVariantVisible?: (skuId: string) => void;
  isWholesale: boolean;
  sortedAttributeGroups: any[];
  maxVariantPrice: number | null; // Dropshipping max price
  minVariantPrice: number | null; // Dropshipping min price
  maxWholesalePrice?: number | null; // Wholesale max price
  minWholesalePrice?: number | null; // Wholesale min price
  groupedVariants: Map<string, any>;
  setTotalWholesalePrice: (price: number) => number;
  isMinMaxRange: boolean;
  customMOQ: number;
  MOQ: number;
  productError?: string | null;
  searchKeyword?: string;
  isLoggedIn?: boolean;
  askForPricingQuotation: boolean;
  isOpenAskInquiry: boolean;
  setIsOpenAskInquiry: (bool: boolean) => void;
  shouldShowWeightMessage?: boolean;
  hasZeroDimensions?: boolean;
  onVariantUrlUpdate?: (variantSkuId: string | number) => void;
  initialVariantId?: string | null;
  singleVariantPriceForTop?: number | null;
  showSingleVariantPriceOnTop?: boolean;
  hasVariantsWithPriceAndDimensions?: boolean;
  [key: string]: any;
}

const ProductDetailRight = ({
  product,
  isDbProduct,
  isFromMongoDB,
  priceRanges,
  setPreferredImage,
  variantDetails,
  isLoading,
  selectedVariants,
  setSelectedVariants,
  pricingMode,
  setPricingMode,
  onQuantityChangeForVariant,
  variantPrices = {},
  onVariantVisible,
  WholesaleInitialQuantity,
  isWholesale,
  sortedAttributeGroups,
  maxVariantPrice,
  minVariantPrice,
  maxWholesalePrice,
  minWholesalePrice,
  groupedVariants,
  setTotalWholesalePrice,
  isMinMaxRange,
  customMOQ,
  MOQ,
  productError,
  searchKeyword,
  isLoggedIn,
  askForPricingQuotation,
  isOpenAskInquiry,
  setIsOpenAskInquiry,
  shouldShowWeightMessage = false,
  hasZeroDimensions = false,
  onVariantUrlUpdate,
  initialVariantId,
  singleVariantPriceForTop = null,
  showSingleVariantPriceOnTop = false,
  hasVariantsWithPriceAndDimensions = false,
  pricingData = {},
}: ProductDetailRightProps) => {
  // Use isFromMongoDB if provided, otherwise fall back to isDbProduct
  const [isProductInDB, setIsProductInDB] = useState(isFromMongoDB ?? isDbProduct ?? false);
  const isDbProductFinal = isProductInDB || (isFromMongoDB ?? isDbProduct ?? false);
  const navigate = useNavigate();
  const [currentSelectedVariant, setCurrentSelectedVariant] = useState(null); // Store complete variant data
  const [selectedAttrValues, setSelectedAttrValues] = useState({});
  const [showAllImages, setShowAllImages] = useState(false);
  const [zoomModal, setZoomModal] = useState<{ show: boolean; image: string; index: number }>({ show: false, image: '', index: 0 });
  const message = useMessage();
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [visibleSpecCount, setVisibleSpecCount] = useState(5);
  const [invalidDescImages, setInvalidDescImages] = useState<Record<number, boolean>>({});
  const [showFullTitle, setShowFullTitle] = useState(false);
  const [isWhatsAppLoading, setIsWhatsAppLoading] = useState(false);
  const [isPhoneCallLoading, setIsPhoneCallLoading] = useState(false); // Added for phone call loading state
  const [isPriceLoading, setIsPriceLoading] = useState(false); // Added for price loading state
  const [hiddenTags, setHiddenTags] = useState<string[]>([]);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionGenerated, setDescriptionGenerated] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // ✅ Desktop: always show full title and never show See more/less
  useEffect(() => {
    if (!isMobile) setShowFullTitle(true);
  }, [isMobile]);

  // Fetch hidden tags on component mount
  useEffect(() => {
    fetch(process.env.REACT_APP_APIUrl + 'product/hidden-tags')
      .then(res => res.json())
      .then(data => {
        if (data?.status?.code === 200) {
          setHiddenTags(data.data?.hiddenTags || []);
        }
      })
      .catch(err => console.error('Failed to fetch hidden tags:', err));
  }, []);
  // Set default pricing mode when component loads - always default to Wholesale
  useEffect(() => {
    if (!isLoading && !isWholesale) {
      setPricingMode(SellingModes.Wholesale);
    }
  }, [isLoading]); // Run when loading state changes

  // // Fetch hidden tags on component mount
  // useEffect(() => {
  //   fetch(process.env.REACT_APP_APIUrl + 'product/hidden-tags')
  //     .then(res => res.json())
  //     .then(data => {
  //       if (data?.status?.code === 200) {
  //         setHiddenTags(data.data?.hiddenTags || []);
  //       }
  //     })
  //     .catch(err => console.error('Failed to fetch hidden tags:', err));
  // }, []);

  // Auto-select variant from URL when page loads with initialVariantId
  useEffect(() => {
    if (initialVariantId && initialVariantId !== '0' && variantDetails && Object.keys(variantDetails).length > 0) {
      // Try to find variant by skuId - handle both string and number keys
      let variant = variantDetails[initialVariantId];

      // If not found directly, search through all variants
      if (!variant) {
        variant = Object.values(variantDetails).find((v: any) => String(v.skuId) === String(initialVariantId));
      }

      if (variant) {
        // Set the current selected variant
        setCurrentSelectedVariant(variant);

        // Set the selected attribute values from the variant
        if (variant.skuAttributes) {
          const attrValues = variant.skuAttributes.reduce((acc: Record<string, string>, attr: any) => {
            acc[attr.attributeId] = attr.valueTrans || attr.value;
            return acc;
          }, {});
          setSelectedAttrValues(attrValues);
        }

        // Set the preferred image if variant has one
        const img = variant?.skuAttributes?.find((attr: any) => attr.skuImageUrl)?.skuImageUrl || variant?.skuImageUrl;
        if (img) setPreferredImage(img);
      }
    }
  }, [initialVariantId, variantDetails]); // Run when variantDetails loads

  // Periodically check if Alibaba product gets stored in DB (for dynamic price color update)
  useEffect(() => {
    // Only check for Alibaba products that aren't already confirmed to be in DB
    if (isDbProductFinal || !product?.sku) {
      return;
    }

    const isAlibabaProduct =
      String(product?.location || '').toLowerCase() === 'alibaba' || String(product?.location || '').toLowerCase() === 'global' || product?.source === 'alibaba';

    if (!isAlibabaProduct || !isLoggedIn) {
      return;
    }

    const identifier = product.offerId || product.sku;
    if (!identifier) {
      return;
    }

    // Initial check immediately, then poll every 2 seconds for up to 40 seconds
    let checkCount = 0;
    const maxChecks = 20; // Check for ~40 seconds (2s intervals)
    let timeoutId: NodeJS.Timeout | null = null;

    const checkIfInDB = async () => {
      if (isProductInDB || checkCount >= maxChecks) {
        return;
      }

      try {
        const { default: AlibabaSubscriptionApiService } = await import('../../../../network/apiServices/AlibabaSubscriptionApiService');
        const res = await AlibabaSubscriptionApiService.checkSubscriptionStatus(identifier);

        if (res && res.status && res.status.code === 200 && res.data) {
          // If source is 'products', product is stored in DB
          if (res.data.source === 'products') {
            setIsProductInDB(true);
            return; // Stop polling once found in DB
          }
        }
      } catch (err) {
        console.error('Error checking if product is in DB:', err);
      }

      checkCount++;

      // Continue polling if not found yet and haven't exceeded max checks
      if (!isProductInDB && checkCount < maxChecks) {
        timeoutId = setTimeout(checkIfInDB, 2000); // Check every 2 seconds
      }
    };

    // Start checking immediately on mount
    checkIfInDB();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [product?.offerId, product?.sku, product?.location, product?.source, isDbProductFinal, isLoggedIn, isProductInDB]);

  // Show price loading effect when in wholesale mode and prices are not ready
  useEffect(() => {
    if (isWholesale && priceRanges.length === 0 && !isLoading) {
      setIsPriceLoading(true);
      // Set a timeout to stop the loading effect after 3 seconds as fallback
      const timer = setTimeout(() => {
        setIsPriceLoading(false);
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setIsPriceLoading(false);
    }
  }, [isWholesale, priceRanges, isLoading]);

  // CTA Handlers - Updated to open in new tab
  const handleWhatsApp = () => {
    setIsWhatsAppLoading(true);

    try {
      const phoneNumber = '918810413147'; // WhatsApp requires country code without +

      // Get product information for the message
      const productTitle = product?.title || 'this product';
      const productUrl = window.location.href;

      // Create a message with only product title and URL (no price)
      const messageText = `Hello! I need help with shopping.\n\nProduct: ${productTitle}\nLink: ${productUrl}\n\nI'm interested in purchasing this product.`;

      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(messageText)}`;

      // Open WhatsApp in a new tab with proper security attributes
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

      // Track the click (you can add analytics here)
      console.log('WhatsApp button clicked for product:', productTitle);

      // Show success message
      // message.showToast('Opening WhatsApp in new tab...', ToastTypes.SUCCESS);
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
      message.showToast('Failed to open WhatsApp. Please try again.', ToastTypes.ERROR);
    } finally {
      setIsWhatsAppLoading(false);
    }
  };

  const handlePhoneCall = () => {
    setIsPhoneCallLoading(true);

    try {
      const phoneNumber = 'tel:+918810413147'; // Telephone requires + country code

      // For phone calls, we need to handle it differently
      // Since tel: links don't work in new tabs, we'll use a workaround
      // We'll create a hidden link and click it programmatically

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = phoneNumber;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);

      // Click the link to trigger the phone app
      link.click();

      // Remove the link from DOM
      document.body.removeChild(link);

      // Show success message
      message.showToast('Opening phone app...', ToastTypes.SUCCESS);
    } catch (error) {
      console.error('Error initiating phone call:', error);
      message.showToast('Failed to open phone app. Please try again.', ToastTypes.ERROR);

      // Fallback: Try to open in new tab for web-based phone services
      try {
        const phoneNumber = 'tel:+918810413147  ';
        window.open(phoneNumber, '_blank', 'noopener,noreferrer');
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setIsPhoneCallLoading(false);
    }
  };

  const productUnit = product.saleInfo?.unit;

  // Helper function to format field names from snake_case to Title Case
  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to check if a value is meaningful (not empty/null/undefined)
  const hasMeaningfulValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'boolean') return true; // Include booleans
    if (typeof value === 'number') return true; // Include numbers (including 0)
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  };

  // Helper function to format field value for display
  const formatFieldValue = (value: any): string => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Helper function to format date for display
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return String(dateValue);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return String(dateValue);
    }
  };

  const allSpecs = useMemo(() => {
    const specsMap = new Map<string, any>();
    const anyProduct: any = product || {};

    // Fields to exclude from dynamic extraction (system/internal fields)
    const excludedFields = new Set([
      '_id',
      '__v',
      'id',
      'vendor_id',
      'catalogId',
      'userSubscribe',
      'tags',
      'variations',
      'productSaleInfo',
      'sellerDataInfo',
      'productAttribute',
      'priceRanges',
      'imgUrl',
      'image',
      'description',
      'img_description',
      'vendor_price',
      'mrp',
      'sp',
      'moq',
      'status',
      'is_delete',
      'is_display',
      'homePageShow',
      'slug',
      'nodeId',
      'categoryPath',
      'categoryId',
      'is_category_approved',
      'category_mapped_by',
      'gst',
      'hsn',
      'barcode',
      'channel',
      'handling_days',
      'warranty',
      'offerId',
      'sku',
      'parentSku',
      'location', // Already handled separately
      'title',
      'brand',
      'manufacturer', // Core product info, not specs
      'feed_product_type',
      'verified_seller',
      'saleInfo',
      'companyName',
      'monthSoldOut',
      'soldOut',
      'c_name',
      'subscribe',
      'cart',
      'cartProductQuantity',
      'local_shipping',
      'quantity',
      'keywordSearch', // Will be displayed separately at the bottom
      'supplierLogo',
      'supplierlogo', // Hide supplier logo from specs
      'consignPrice',
      'consignprice',
      'cp', // Hide consign price from specs
      'globalapiresponse',
      'globalApiResponse', // Hide raw API payload from specs
    ]);

    // Country of Origin → fixed ROC
    if (anyProduct.location) {
      const key = 'country of origin';
      specsMap.set(key, {
        attributeId: 'origin',
        attributeNameTrans: 'Country of Origin',
        valueList: ['ROC'], // fixed value
      });
    }

    // Extract from productAttribute array (existing logic)
    if (Array.isArray(anyProduct.productAttribute)) {
      anyProduct.productAttribute.forEach((attr: any) => {
        const label = attr.attributeNameTrans || attr.attributeName;
        const rawValue = attr.valueTrans || attr.value;
        if (!label || !rawValue) return;

        const key = label.trim().toLowerCase();
        if (key.includes('country of origin')) return;

        const existing = specsMap.get(key);
        if (existing) {
          if (!existing.valueList.includes(rawValue)) {
            existing.valueList.push(rawValue);
          }
        } else {
          specsMap.set(key, {
            ...attr,
            valueList: [rawValue],
          });
        }
      });
    }

    // For MongoDB products, extract all additional fields dynamically
    if (isDbProductFinal) {
      // Add First Date Available (createdAt) for database products
      if (anyProduct.createdAt) {
        const formattedDate = formatDate(anyProduct.createdAt);
        if (formattedDate) {
          specsMap.set('first date available', {
            attributeId: 'createdAt',
            attributeNameTrans: 'First Date Available',
            attributeName: 'createdAt',
            valueTrans: formattedDate,
            value: formattedDate,
            valueList: [formattedDate],
          });
        }
      }

      // Track field names already added from productAttribute to avoid duplicates
      // Check both attributeId and attributeName to catch all variations
      const existingAttributeFieldNames = new Set<string>();
      if (Array.isArray(anyProduct.productAttribute)) {
        anyProduct.productAttribute.forEach((attr: any) => {
          if (attr.attributeId) existingAttributeFieldNames.add(String(attr.attributeId).toLowerCase());
          if (attr.attributeName) existingAttributeFieldNames.add(attr.attributeName.toLowerCase());
          const label = attr.attributeNameTrans || attr.attributeName;
          if (label) existingAttributeFieldNames.add(label.trim().toLowerCase());
        });
      }

      // Iterate through all product fields
      Object.keys(anyProduct).forEach(fieldName => {
        // Skip excluded fields and date fields (createdAt already handled above, updatedAt excluded)
        if (excludedFields.has(fieldName) || excludedFields.has(fieldName.toLowerCase())) return;
        if (fieldName === 'createdAt' || fieldName === 'updatedAt') return;

        const value = anyProduct[fieldName];

        // Only include fields with meaningful values
        if (!hasMeaningfulValue(value)) return;

        // Format the field name for display
        const formattedLabel = formatFieldName(fieldName);
        const key = formattedLabel.toLowerCase();

        // Skip if already added from productAttribute (check both field name and formatted label)
        if (existingAttributeFieldNames.has(fieldName.toLowerCase()) || existingAttributeFieldNames.has(key) || specsMap.has(key)) return;

        // Format the value for display
        const formattedValue = formatFieldValue(value);

        // Add to specs map
        specsMap.set(key, {
          attributeId: fieldName,
          attributeNameTrans: formattedLabel,
          attributeName: fieldName,
          valueTrans: formattedValue,
          value: formattedValue,
          valueList: [formattedValue],
        });
      });
    }

    const specs: any[] = Array.from(specsMap.values()).map((item: any) => ({
      ...item,
      valueTrans: (item.valueList || []).join(', '),
    }));

    // Sort specs to show date fields first for database products
    if (isDbProductFinal) {
      specs.sort((a, b) => {
        const aId = (a.attributeId || '').toString().toLowerCase();
        const bId = (b.attributeId || '').toString().toLowerCase();

        // Priority order: createdAt first, then others
        if (aId === 'createdat' && bId !== 'createdat') return -1;
        if (aId !== 'createdat' && bId === 'createdat') return 1;

        return 0; // Keep original order for others
      });
    }

    return specs;
  }, [product, isDbProductFinal]);

  const visibleSpecs = useMemo(() => allSpecs.slice(0, visibleSpecCount), [allSpecs, visibleSpecCount]);
  useEffect(() => {
    setVisibleSpecCount(5);
  }, [product]);

  // grouped/price data is passed from parent via props now

  // Initialize defaults & reset on pricing mode change
  // Skip if initialVariantId is provided - let the initialVariantId useEffect handle selection
  useEffect(() => {
    if (sortedAttributeGroups?.length && !Object.keys(selectedAttrValues).length && !initialVariantId) {
      setSelectedAttrValues(
        sortedAttributeGroups.reduce((acc: Record<string, string>, grp: any) => {
          const first = Array.from(grp.values.keys())[0] as string;
          if (first) acc[grp.id] = first;
          return acc;
        }, {})
      );
    }
  }, [sortedAttributeGroups, selectedAttrValues, initialVariantId]);

  useEffect(() => setSelectedVariants({}), [pricingMode]);

  // Quantity change handler - updates variant and auto-selects item
  const handleQuantityChangeWithBulkSelection = (skuId: string | number, quantity: number, variant: Variation) => {
    onQuantityChangeForVariant(skuId, quantity, variant);
    const lastAttrGroup = sortedAttributeGroups[sortedAttributeGroups.length - 1];
    const qtyAttr = lastAttrGroup && variant.skuAttributes?.find((attr: any) => String(attr.attributeId) === String(lastAttrGroup.id));
    if (qtyAttr) setSelectedAttrValues(prev => ({ ...prev, [lastAttrGroup.id]: qtyAttr.valueTrans || qtyAttr.value }));
  };

  // Variant selection handler
  const handleVariantSelect = async ({ color, spec, variantId, variant }) => {
    setCurrentSelectedVariant(variant);
    setSelectedAttrValues(prev => variant.skuAttributes?.reduce((acc, attr) => ({ ...acc, [attr.attributeId]: attr.valueTrans || attr.value }), { ...prev }) || prev);
    const img = variant?.skuAttributes?.find(attr => attr.skuImageUrl)?.skuImageUrl || variant?.skuImageUrl;
    if (img) setPreferredImage(img);

    // Update URL with selected variant's skuId
    if (onVariantUrlUpdate && variant?.skuId) {
      onVariantUrlUpdate(variant.skuId);
    }
  };

  const createMarkup = (html: string) => ({ __html: prepareDescriptionHtml(html) });

  // Extract images from description HTML - memoized
  // For DB products, use img_description; for Alibaba API/Solr, use description
  const descriptionImages = useMemo(() => {
    const descHtml = isDbProductFinal ? product.img_description || product.description || '' : product.description || '';
    return extractImagesFromDescription(descHtml);
  }, [product.description, product.img_description, isDbProductFinal]);

  // Image handlers
  const handleImageClick = (imageSrc: string, index: number) => setZoomModal({ show: true, image: imageSrc, index });
  const handlePrevImage = () => {
    const prevIndex = zoomModal.index > 0 ? zoomModal.index - 1 : descriptionImages.length - 1;
    setZoomModal({ show: true, image: descriptionImages[prevIndex], index: prevIndex });
  };
  const handleNextImage = () => {
    const nextIndex = zoomModal.index < descriptionImages.length - 1 ? zoomModal.index + 1 : 0;
    setZoomModal({ show: true, image: descriptionImages[nextIndex], index: nextIndex });
  };
  const handleModalClose = () => setZoomModal({ show: false, image: '', index: 0 });

  // Handle keyword click - navigate to tag route
  const handleKeywordClick = (keyword: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) return;

    // Navigate to tag route
    navigate(`/tag/${encodeURIComponent(trimmedKeyword)}`);
  };

  // Handle generate description API call
  const handleGenerateDescription = async () => {
    if (!product?._id && !product?.id) {
      message.showToast('Product ID not found', ToastTypes.ERROR);
      return;
    }

    setIsGeneratingDescription(true);
    try {
      const productId = product._id || product.id;

      const result = await ProductsApiService.generateDescription(productId);

      if (result.status?.code === 200 && result.data?.description) {
        // Update the product's description in the local state
        // This will trigger a re-render and show the new description
        if (product) {
          product.description = result.data.description;
        }

        message.showToast('Description generated successfully!', ToastTypes.SUCCESS);

        // Force a re-render by updating a dummy state or using a key
        // We'll use a small trick to force component re-render
        setIsGeneratingDescription(false);

        // Optional: You might want to refresh the product data from parent
        // For now, we'll just update the local product object
      } else {
        throw new Error(result.message || 'Failed to generate description');
      }
    } catch (error) {
      console.error('[handleGenerateDescription] Error:', error);
      message.showToast('Failed to fetch description. Please try again.', ToastTypes.ERROR);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Get current price to display based on mode
  const getCurrentPrice = () => {
    if (!variantDetails || Object.keys(variantDetails).length === 0) {
      // Products without variants: use priceRanges.finalPrice as dropship price so we show it instead of "Loading..."
      if (!isWholesale && priceRanges?.length > 0) {
        const rangeFinalPrice = (priceRanges[0] as any)?.finalPrice;
        if (typeof rangeFinalPrice === 'number' && rangeFinalPrice > 0) {
          return rangeFinalPrice;
        }
      }
      // For database products, use product.sp as fallback when no variants are loaded yet
      if (isDbProductFinal && product?.sp && typeof product.sp === 'number' && product.sp > 0) {
        return product.sp;
      }
      return null;
    }
    const variants = Object.values(variantDetails);

    if (isWholesale) {
      // For DB products, use wholesaleFinalPrice (pre-calculated from cp)
      // For API products, fall back to consignPrice/offerPrice
      const minPrice = Math.min(
        ...variants.map((v: Variation) => {
          // Prioritize wholesaleFinalPrice for DB products
          if (v.wholesaleFinalPrice && v.wholesaleFinalPrice > 0) {
            return v.wholesaleFinalPrice;
          }
          return parseFloat(v.consignPrice || '0') || parseFloat(v.fenxiaoPriceInfo?.offerPrice || '0');
        })
      );
      return minPrice !== Infinity ? minPrice : null;
    } else {
      // For dropshipping, use calculated final prices from variantPrices
      // variantPrices contains the dropshipping calculated prices with correct RMB conversion
      if (variantPrices && Object.keys(variantPrices).length > 0) {
        const dropshippingPrices = Object.values(variantPrices)
          .map((vp: any) => vp?.finalPrice)
          .filter((price): price is number => typeof price === 'number' && price > 0);

        if (dropshippingPrices.length > 0) {
          return Math.min(...dropshippingPrices);
        }
      }

      // Fallback to base prices while calculating
      const minPrice = Math.min(
        ...variants.map((v: Variation) => v.finalPrice || parseFloat(v.fenxiaoPriceInfo?.onePiecePrice || v.consignPrice || v.fenxiaoPriceInfo?.offerPrice || '0'))
      );

      // For database products, use product.sp as fallback if no calculated price
      if (minPrice === Infinity || !minPrice || minPrice === 0) {
        if (isDbProductFinal && product?.sp && typeof product.sp === 'number' && product.sp > 0) {
          return product.sp;
        }
        return null;
      }

      return minPrice;
    }
  };

  const sp = minVariantPrice;

  // Check if isOnePsale is false in tagInfoList
  const isDropshippingDisabled = useMemo(() => {
    const tagInfoList = product?.tagInfoList || [];
    const isOnePsaleTag = tagInfoList.find((tag: any) => tag.key === 'isOnePsale');
    return isOnePsaleTag?.value === false || isOnePsaleTag?.value === 'false';
  }, [product?.tagInfoList]);

  // Force Wholesale mode if Dropshipping is disabled
  useEffect(() => {
    if (isDropshippingDisabled && !isWholesale) {
      setPricingMode(SellingModes.Wholesale);
    }
  }, [isDropshippingDisabled, isWholesale, setPricingMode]);

  // Helper to get Wholesale MOQ display value
  const getWholesaleMOQDisplay = () => {
    const actualProductMOQ = Number(
      MOQ ||
        product?.moq ||
        product?.minOrderQuantity ||
        product?.min_order_quantity ||
        product?.productSaleInfo?.minOrderQuantity ||
        0
    );
    // Show actual wholesale MOQ when it is > 1 (tier startQuantity may still be 2)
    if (actualProductMOQ > 1) {
      return actualProductMOQ;
    }

    // If dropshipping is not available, always use actual MOQ (don't convert 1 to 2)
    if (isDropshippingDisabled) {
      const actualMOQ = MOQ || customMOQ || 0;
      return actualMOQ;
    }

    // If dropshipping is available, check priceRanges first
    if (Array.isArray(priceRanges) && priceRanges.length > 0) {
      const firstRange: any = priceRanges[0];
      // Handle both camelCase and snake_case just in case
      const rawStartQty = (firstRange as any).startQuantity ?? (firstRange as any).start_quantity ?? 0;
      const startQty = Number(rawStartQty) || 0;

      if (startQty === 1) {
        return 2; // Convert 1 to 2 when dropshipping is available
      }
      return startQty;
    }

    // Fallback to existing MOQ/customMOQ logic
    const displayMOQ = MOQ || customMOQ || 0;
    if (displayMOQ === 1) return 2; // Convert 1 to 2 when dropshipping is available
    return displayMOQ;
  };

  const handleChangePriceMode = () => {
    // If we're in Wholesale mode (isWholesale === true), we're trying to switch TO Dropshipping
    if (isDropshippingDisabled && isWholesale) {
      message.showToast(`1-Piece is not available for this product`, ToastTypes.WARNING);
      return;
    }
    if (MOQ > 1) {
      message.showToast(`1-Piece is not available...`, ToastTypes.INFO);
      return;
    }
    setPricingMode(isWholesale ? SellingModes.DropShipping : SellingModes.Wholesale);
  };
  const TITLE_LIMIT = 60;
  const fullTitle = product?.title || '';
  const isLongTitle = fullTitle.length > TITLE_LIMIT;
  const truncatedTitle = isLongTitle ? fullTitle.slice(0, TITLE_LIMIT) + '...' : fullTitle;

  const startingDropshippingPrice = getCurrentPrice();

  if (isLoading) return <ProductDetailRightSkeleton />;

  // Show error UI if product error exists
  if (productError) {
    return (
      <div className="product_detail_right_section">
        <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-100">
          <div className="flex flex-col items-center justify-center text-center py-12">
            <div className="mb-4">
              <svg className="w-20 h-20 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.172 16.172a4 4 0 0 1 5.656 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Product Not Found</h2>
            <p className="text-gray-600 mb-6 max-w-md">{productError === '商品不存在' ? 'The product does not exist.' : productError}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => window.history.back()} className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm">
                Go Back
              </button>
              <button onClick={() => navigate('/collection')} className="px-6 py-2.5 bg-[#3DD0E5] text-white rounded-lg hover:bg-[#2bb8cc] transition-colors font-medium text-sm">
                Browse Products
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product_detail_right_section px-2 sm:px-3 md:px-4">
      {/* Main Product Card */}
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-3 border border-gray-100">
        {/* Product Title */}
        <div className="mb-3">
          <p className="pd_product_name text-[15px] sm:text-[16px] font-semibold leading-tight text-gray-900">
            {/* Desktop: always full title | Mobile: existing behavior */}
            {!isMobile ? fullTitle : isLongTitle && !showFullTitle ? truncatedTitle : fullTitle}

            {/* Mobile-only: See more */}
            {isMobile && isLongTitle && !showFullTitle && (
              <button type="button" onClick={() => setShowFullTitle(true)} className="ml-1 text-[11px] font-normal text-[#3DD0E5] hover:text-[#24b5cb] inline align-baseline">
                See more
              </button>
            )}
          </p>

          {/* Mobile-only: See less */}
          {isMobile && isLongTitle && showFullTitle && (
            <button type="button" onClick={() => setShowFullTitle(false)} className="mt-1 text-[11px] font-normal text-[#3DD0E5] hover:text-[#24b5cb]">
              See less
            </button>
          )}
        </div>

        {/* <div className="pricing-mode-switch-container justify-between pr-1">
          <div className="flex justify-between align-center gap-2">
            <span className={`pricing-mode-label ${!isWholesale ? 'active' : ''}`}>DropShipping</span>
            <Switch checked={isWholesale} onChange={handleChangePriceMode} className="pricing-mode-switch" />
            <span className={`pricing-mode-label ${isWholesale ? 'active' : ''}`}>Wholesale</span>
          </div>

        </div>
          */}
        <div className="pricing-mode-switch-container mb-3 sm:mb-4">
          <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 w-full min-h-[44px]">
            {/* DropShipping button */}
            <button
              type="button"
              onClick={() => {
                if (isWholesale) {
                  if (isDropshippingDisabled) {
                    message.showToast('1-Piece is not available for this product', ToastTypes.WARNING);
                    return;
                  }
                  handleChangePriceMode();
                }
              }}
              className={`
                flex-1 px-1.5 py-2 text-[11px] sm:text-xs font-medium rounded-full transition text-center
                ${!isWholesale ? 'bg-black text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}
                min-h-[36px] flex items-center justify-center
              `}
            >
              <span className="flex flex-row leading-tight">
                <span className="text-[13px] sm:text-[14px] font-semibold text-[#2563eb]">One-Piece</span>
                {/* <span className="text-[9px] sm:text-[10px] opacity-90 mt-0.5">(MOQ 1)</span> */}
              </span>
            </button>
            <Switch checked={isWholesale} onChange={handleChangePriceMode} className="pricing-mode-switch" />
            {/* Wholesale button */}
            <button
              type="button"
              onClick={() => {
                if (!isWholesale) {
                  handleChangePriceMode();
                }
              }}
              className={`
                flex-1 px-1.5 py-2 text-[11px] sm:text-xs font-medium rounded-full transition text-center
                ${isWholesale ? 'bg-black text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}
                min-h-[36px] flex items-center justify-center
              `}
            >
              <span className="flex flex-row leading-tight">
                <span className="text-[12px] sm:text-[13px]">Wholesale</span>
                <span className="text-[9px] sm:text-[10px] opacity-90 mt-0.5">(MOQ {getWholesaleMOQDisplay()})</span>
              </span>
            </button>
          </div>
        </div>

        {/* When weight/dimension is 0: show price if any variant has price + dimensions, else red message or single variant price */}
        {askForPricingQuotation ? (
          (() => {
            const effectiveMin = isWholesale ? (minWholesalePrice ?? minVariantPrice) : minVariantPrice;
            const effectiveMax = isWholesale ? (maxWholesalePrice ?? maxVariantPrice) : maxVariantPrice;
            const hasVariantPriceRange = hasVariantsWithPriceAndDimensions && effectiveMin != null && effectiveMax != null && effectiveMin > 0;
            const hasRange = hasVariantPriceRange && effectiveMin !== effectiveMax;

            if (hasVariantPriceRange) {
              return (
                <div className="my-2 sm:my-3">
                  <div className="flex flex-col items-start">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-start">
                        <div className="text-[11px] text-gray-500 leading-none" style={{ marginBottom: '-4px' }}>
                          {hasRange ? 'Min' : 'Starting price'}
                        </div>
                        <div className="text-2xl font-medium" style={{ color: isDbProductFinal ? '#0066cc' : '#000000' }}>
                          {`₹${effectiveMin.toFixed(2)}`}
                          <span className="hidden md:inline text-[11px] text-gray-600 ml-1">/ {productUnit || 'piece'}</span>
                        </div>
                      </div>
                      {hasRange && (
                        <>
                          <div className="text-2xl inline-block font-medium" style={{ color: isDbProductFinal ? '#0066cc' : '#000000' }}>
                            -
                          </div>
                          <div className="flex flex-col items-start">
                            <div className="text-[11px] text-gray-500 leading-none" style={{ marginBottom: '-4px' }}>
                              Max
                            </div>
                            <div className="text-2xl inline-block font-medium" style={{ color: isDbProductFinal ? '#0066cc' : '#000000' }}>
                              {`₹${effectiveMax.toFixed(2)}`}
                              <span className="hidden md:inline text-[11px] text-gray-600 ml-1">/ {productUnit || 'piece'}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            if (showSingleVariantPriceOnTop && singleVariantPriceForTop != null && singleVariantPriceForTop > 0) {
              return (
                <div className="my-2 sm:my-3">
                  <div className="text-[11px] text-gray-500 leading-none" style={{ marginBottom: '-4px' }}>
                    Price for variant with dimensions
                  </div>
                  <div className="text-2xl font-medium" style={{ color: isDbProductFinal ? '#0066cc' : '#000000' }}>
                    ₹{singleVariantPriceForTop.toFixed(2)}
                    <span className="hidden md:inline text-[11px] text-gray-600 ml-1">/ {productUnit || 'piece'}</span>
                  </div>
                </div>
              );
            }
            return (
              <div className="my-2 sm:my-3">
                <span className="text-[13px] sm:text-sm text-red-600 font-medium block">To get price info send inquiry</span>
              </div>
            );
          })()
        ) : (
          <>
            {isWholesale && priceRanges.length > 0 && (
              <PriceRangeDisplay
                isDbProduct={isDbProductFinal}
                customMOQ={customMOQ}
                isWholesale={isWholesale}
                priceRanges={priceRanges}
                maxVariantPrice={maxWholesalePrice || maxVariantPrice}
                minVariantPrice={minWholesalePrice || minVariantPrice}
                WholesaleInitialQuantity={WholesaleInitialQuantity}
                productUnit={productUnit}
                askForPricingQuotation={askForPricingQuotation}
                shouldShowWeightMessage={shouldShowWeightMessage}
                hasZeroDimensions={hasZeroDimensions}
              />
            )}

            {/* Show price loading effect when in wholesale mode and prices are not ready */}
            {isWholesale && priceRanges.length === 0 && isPriceLoading && (
              <div className="my-2 sm:my-3">
                <div className="text-lg sm:text-xl font-medium text-gray-500 flex items-center">
                  <span className="mr-2">Loading wholesale prices</span>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
                <div className="text-xs text-gray-600 mt-1">Please wait while we fetch the best wholesale prices...</div>
              </div>
            )}

            {!isWholesale && (
              <div className="my-2 sm:my-3">
                {Object.keys(variantPrices).length === 0 &&
                  !startingDropshippingPrice &&
                  !((priceRanges?.[0] as any)?.finalPrice > 0) &&
                  !(isDbProductFinal && product?.sp && typeof product.sp === 'number' && product.sp > 0) ? (
                  <>
                    <div className="text-lg sm:text-xl font-medium text-gray-500 flex items-center">
                      <span className="mr-2">Loading 1-Piece prices</span>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Please wait while we fetch the best 1-Piece prices...</div>
                  </>
                ) : startingDropshippingPrice ? (
                  (() => {
                    // Calculate min and max dropshipping prices from variantPrices
                    const dropshippingPriceValues = Object.values(variantPrices)
                      .map((vp: any) => vp?.finalPrice)
                      .filter((price): price is number => typeof price === 'number' && price > 0);

                    const minDropshippingPrice = dropshippingPriceValues.length > 0 ? Math.min(...dropshippingPriceValues) : startingDropshippingPrice;
                    const maxDropshippingPrice = dropshippingPriceValues.length > 0 ? Math.max(...dropshippingPriceValues) : startingDropshippingPrice;
                    const hasRange = minDropshippingPrice !== maxDropshippingPrice;

                    return (
                      <div className="flex flex-col items-start">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col items-start">
                            <div className="text-[11px] text-gray-500 leading-none" style={{ marginBottom: '-4px' }}>
                              {hasRange ? 'Min' : 'Starting price'}
                            </div>
                            <div className="text-2xl font-medium" style={{ color: isDbProductFinal ? '#0066cc' : '#000000' }}>
                              {`₹${minDropshippingPrice.toFixed(2)}`}
                              <span className="hidden md:inline text-[11px] text-gray-600 ml-1">/ {productUnit || 'piece'}</span>
                            </div>
                          </div>
                          {hasRange && (
                            <>
                              <div className="text-2xl inline-block font-medium" style={{ color: isDbProductFinal ? '#0066cc' : '#000000' }}>
                                -
                              </div>
                              <div className="flex flex-col items-start">
                                <div className="text-[11px] text-gray-500 leading-none" style={{ marginBottom: '-4px' }}>
                                  Max
                                </div>
                                <div className="text-2xl inline-block font-medium" style={{ color: isDbProductFinal ? '#0066cc' : '#000000' }}>
                                  {`₹${maxDropshippingPrice.toFixed(2)}`}
                                  <span className="hidden md:inline text-[11px] text-gray-600 ml-1">/ {productUnit || 'piece'}</span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Message directly below price - hide when variants have price and non-zero weight/dimensions */}
                        {(askForPricingQuotation || shouldShowWeightMessage || hasZeroDimensions) && !hasVariantsWithPriceAndDimensions && (
                          <span className="text-[11px] sm:text-xs text-gray-500 leading-none block mt-1 w-full text-left">
                            Weight or dimensions not available. Send an enquiry for pricing and details.
                          </span>
                        )}
                      </div>
                    );
                  })()
                ) : null}
              </div>
            )}
          </>
        )}

        {/* ✅ Mobile-only note just below product price (HIDE when Send Enquiry is shown) */}
        {isMobile && !askForPricingQuotation && (
          <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-[12px] text-gray-700 font-medium leading-tight">
                  Note: Prices are fetched in real-time from suppliers. The final price may vary based on current supplier pricing.
                </p>
              </div>
            </div>
          </div>
        )}

        <DeliveryDateBadge />
      </div>

      {/* CTA Buttons Section - White Background, Black Text, Colorful Icons */}
      <div className="bg-white rounded-lg shadow-sm p-2 sm:p-4 mb-3 border border-gray-100">
        {/* UPDATED: "Need Help?" and timing aligned above-middle of Call Us */}
        <div className="flex items-start justify-between mb-1 sm:mb-4">
          <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-800">Need Help?</h2>

          {/* This wrapper makes the timing text sit in the same "column" as Call Us */}
          <div className="flex-1">
            <div className="flex flex-row gap-2 sm:gap-3">
              <div className="flex-1" />
              <div className="flex-1 flex justify-center">
                <span className="text-[10px] sm:text-xs text-gray-500 font-medium text-center">Mon to Sat (9:30AM to 6:00PM)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons in one row */}
        <div className="flex flex-row gap-2 sm:gap-3">
          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsApp}
            disabled={isWhatsAppLoading}
            className="flex items-center justify-center gap-2 bg-white hover:bg-green-50 border border-gray-200 hover:border-green-400 rounded-lg py-2.5 px-3 sm:px-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
          >
            <FaWhatsapp className="text-green-500 text-lg sm:text-xl" />
            <span className="text-gray-900 font-medium text-xs sm:text-sm whitespace-nowrap">Chat Now</span>
          </button>

          {/* Phone Call Button */}
          <button
            onClick={handlePhoneCall}
            disabled={isPhoneCallLoading}
            className="flex items-center justify-center gap-2 bg-white hover:bg-blue-50 border border-gray-200 hover:border-blue-400 rounded-lg py-2.5 px-3 sm:px-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
          >
            <FaPhoneAlt className="text-blue-500 text-lg sm:text-xl" />
            <span className="text-gray-900 font-medium text-xs sm:text-sm whitespace-nowrap">Call Us</span>
          </button>
        </div>
      </div>

      {/* MOBILE ONLY Subscribe Box */}
      <div className="block md:hidden">
        {isMobile && pricingMode === 'dropshipping' && (
          <div className="bg-white rounded-lg shadow-sm p-3 mb-3 border border-gray-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <div className="flex-1">
                <div className="font-semibold text-[14px] text-gray-900 mb-0.5">Want to Sell This Product?</div>
                <div className="text-[11px] text-gray-600">Subscribe to list this product in your store</div>
              </div>
              <div className="w-full sm:w-auto mt-2 sm:mt-0">
                <SubscribeButton product={product} searchKeyword={searchKeyword} isLoggedIn={!!isLoggedIn} sp={sp} priceRanges={priceRanges} variantDetails={variantDetails} />
              </div>
            </div>
          </div>
        )}
      </div>

      <VariantList
        productUnit={productUnit}
        isDbProduct={isDbProductFinal}
        variantDetails={variantDetails}
        groupedVariants={groupedVariants}
        sortedAttributeGroups={sortedAttributeGroups}
        selectedAttrValues={selectedAttrValues}
        setSelectedAttrValues={setSelectedAttrValues}
        onVariantSelect={handleVariantSelect}
        onQuantityChange={(skuId, quantity, variant) => handleQuantityChangeWithBulkSelection(skuId, quantity, variant)}
        pricingMode={pricingMode}
        selectedVariants={selectedVariants}
        priceRanges={priceRanges}
        variantPrices={variantPrices}
        onVariantVisible={onVariantVisible}
        isWholesale={isWholesale}
        isMinMaxRange={isMinMaxRange}
        setTotalWholesalePrice={setTotalWholesalePrice}
        MOQ={MOQ}
        customMOQ={customMOQ}
        product={product}
        initialVariantId={initialVariantId}
        askForPricingQuotation={askForPricingQuotation}
      />

      {/* Hidden for now */}
      {/* <SupplierInfoSection product={product} /> */}

      <ContactSupplierSection product={product} isOpen={isOpenAskInquiry} setIsOpen={setIsOpenAskInquiry} />

      {/* <h1 className="pd_head">Product Description</h1>

      <div className="specifications_enhanced">
        <div className="spec_header">Product Specifications</div>
        <table className="spec_table">
          {product.sku ? (
            <tr>
              <td>Sku</td>
              <td>{product.sku}</td>
            </tr>
          ) : null}
          {product.location ? (
            <tr>
              <td>Country of Origin</td>
              <td>{product.location}</td>
            </tr>
          ) : null}
        </table>
      </div> */}

      {/* <h1 className="pd_head">Product Description</h1> */}

      {allSpecs.length > 0 && (
        <div className="specifications_enhanced mt-0 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="spec_header flex items-center justify-between px-3 py-1 border-b border-gray-100">
            <div>
              <span className="text-[13px] sm:text-[14px] font-semibold text-gray-900">Product Specifications</span>
            </div>
          </div>

          {/* Table */}
          <div className="max-h-[360px] md:max-h-[420px] overflow-y-auto custom-scrollbar">
            <table className="spec_table w-full border-collapse">
              <tbody>
                {visibleSpecs
                  .filter((attr: any) => {
                    const label = (attr.attributeNameTrans || attr.attributeName)?.toLowerCase() || '';
                    const attrId = (attr.attributeId || '').toString().toLowerCase();
                    if (label.includes('price segment')) return false;
                    if (label.includes('brand')) return false;
                    if (attrId === 'sku') return false;
                    // Hide keywordSearch from regular specs as it will be shown at the bottom
                    if (attrId === 'keywordsearch' || label.includes('keyword search')) return false;
                    // Hide Supplierlogo and Consign Price
                    if (label.includes('supplierlogo') || label.includes('supplier logo')) return false;
                    if (label.includes('consign price') || label.includes('consignprice')) return false;
                    // Hide thumbnail/image-related specs and internal fields
                    if (label.includes('iswhiteimageavailable') || label.includes('thumbnailimage')) return false;
                    // Hide individual category ID fields since we show them combined with names
                    if (label.includes('top category') || label.includes('topcategory') || attrId === 'topcategoryid') return false;
                    if (label.includes('second category') || label.includes('secondcategory') || attrId === 'secondcategoryid') return false;
                    if (label.includes('third category') || label.includes('thirdcategory') || attrId === 'thirdcategoryid') return false;
                    const hiddenFields = [
                      'selleropenid',
                      'tradescore',
                      'taginfolist',
                      'traceinfo',
                      'topcategoryid',
                      'secondcategoryid',
                      'thirdcategoryid',
                      'mainvideo',
                      'iswhiteimageavailable',
                      'thumbnailimage',
                      'globalapiresponse',
                      'lowfbp',
                      'lowestcalculatedfbp',
                    ];
                    if (hiddenFields.includes(label.replace(/\s+/g, '')) || hiddenFields.includes(attrId)) return false;
                    return true;
                  })
                  .map((attr: any, index: number) => (
                    <tr key={`${attr.attributeId || 'spec'}-${index}`} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                      <td className="w-1/3 px-3 py-2 sm:py-2.5 font-medium text-gray-500 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                        {attr.attributeNameTrans || attr.attributeName}
                      </td>
                      <td className="px-3 py-2 sm:py-2.5 text-gray-800 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                        {attr.valueTrans || attr.value}
                      </td>
                    </tr>
                  ))}
                {/* Show Category Info if available */}
                {(product?.topCategoryId || product?.secondCategoryId || product?.thirdCategoryId) && (
                  <>
                    {product?.topCategoryId && (
                      <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                        <td className="w-1/3 px-3 py-2 sm:py-2.5 font-medium text-gray-500 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                          Top Category
                        </td>
                        <td className="px-3 py-2 sm:py-2.5 text-gray-800 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                          {product?.topCategoryName ? `${product.topCategoryName} (ID: ${product.topCategoryId})` : product.topCategoryId}
                        </td>
                      </tr>
                    )}
                    {product?.secondCategoryId && (
                      <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                        <td className="w-1/3 px-3 py-2 sm:py-2.5 font-medium text-gray-500 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                          Second Category
                        </td>
                        <td className="px-3 py-2 sm:py-2.5 text-gray-800 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                          {product?.secondCategoryName ? `${product.secondCategoryName} (ID: ${product.secondCategoryId})` : product.secondCategoryId}
                        </td>
                      </tr>
                    )}
                    {product?.thirdCategoryId && (
                      <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                        <td className="w-1/3 px-3 py-2 sm:py-2.5 font-medium text-gray-500 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                          Third Category
                        </td>
                        <td className="px-3 py-2 sm:py-2.5 text-gray-800 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                          {product?.thirdCategoryName ? `${product.thirdCategoryName} (ID: ${product.thirdCategoryId})` : product.thirdCategoryId}
                        </td>
                      </tr>
                    )}
                  </>
                )}
                {/* Show Category Info if available */}
                {pricingData?.categoryInfo?.id && (
                  <>
                    <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                      <td className="w-1/3 px-3 py-2 sm:py-2.5 font-medium text-gray-500 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                        PL
                      </td>
                      <td className="px-3 py-2 sm:py-2.5 text-gray-800 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                        {pricingData.categoryInfo.name} (ID: {pricingData.categoryInfo.id})
                      </td>
                    </tr>
                  </>
                )}
                {/* Display keywordSearch tags at the bottom (excluding hidden tags and tags with 4+ digit suffix e.g. apple-32423) */}
                {(() => {
                  // Hide tags that end with hyphen + 4+ digits (e.g. apple-32423); show apple-1, apple-2, apple-3
                  const hasLongSuffixNumber = (tag: string) => /-\d{4,}$/.test((tag || '').trim());
                  const visibleTags = product?.keywordSearch?.filter((tag: string) => !hiddenTags.includes(tag) && !hasLongSuffixNumber(tag)) || [];

                  return visibleTags.length > 0 ? (
                    <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                      <td className="w-1/3 px-3 py-2 sm:py-2.5 font-medium text-gray-500 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                        Tags
                      </td>
                      <td className="px-3 py-2 sm:py-2.5 text-gray-800 align-top" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                        <div className="flex flex-wrap gap-2">
                          {visibleTags.map((tag: string, idx: number) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={e => handleKeywordClick(tag, e)}
                              className="inline-flex items-center px-3 py-1 rounded-full border-2 border-[#3DD0E5] text-[#3DD0E5] hover:border-[#24b5cb] hover:text-[#24b5cb] cursor-pointer transition-colors bg-transparent hover:bg-[#24b5cb]/5"
                              title={`Search for ${tag}`}
                              style={{
                                fontSize: '12px',
                                lineHeight: '1.2',
                                outline: 'none',
                              }}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ) : null;
                })()}
              </tbody>
            </table>
          </div>

          {/* Expand / Collapse button */}
          {allSpecs.length > 5 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50/70 flex justify-center">
              {(() => {
                const INITIAL_COUNT = 5;
                const hasMore = visibleSpecCount < allSpecs.length;

                const handleToggle = () => {
                  if (hasMore) {
                    setVisibleSpecCount(allSpecs.length);
                  } else {
                    setVisibleSpecCount(INITIAL_COUNT);
                  }
                };

                return (
                  <button
                    type="button"
                    onClick={handleToggle}
                    className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-teal-700 hover:text-teal-600 bg-white px-3 py-1.5 rounded-full shadow-sm hover:shadow-md transition-all border border-teal-100"
                  >
                    {hasMore ? `Show all specifications (${allSpecs.length - INITIAL_COUNT})` : 'Show less specifications'}
                    <span className="text-[10px]">{hasMore ? '▼' : '▲'}</span>
                  </button>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Thumbnail images in PDP product specification area hidden per requirement */}
      {descriptionImages.length > 0 && (
        <div className="mt-4 sm:mt-6">
          <h3 className="product_images_heading text-[15px] sm:text-[16px] font-semibold mb-2 px-1">Product Images</h3>
          <div className="description_thumbnails">
            {(showAllImages ? descriptionImages : descriptionImages.slice(0, 8)).map((img, index) => {
              if (!img || (typeof img === 'string' && img.trim() === '') || invalidDescImages[index]) {
                return null;
              }
              return (
                <div
                  key={index}
                  className="desc_thumbnail_container aspect-square relative rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                  onClick={() => (index === 7 && !showAllImages && descriptionImages.length > 8 ? setShowAllImages(true) : handleImageClick(img, index))}
                >
                  <img
                    src={appendSizeToImageUrl(img, `200x200`)}
                    alt={`Description image ${index + 1}`}
                    className="desc_thumbnail w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    onError={() => setInvalidDescImages(prev => ({ ...prev, [index]: true }))}
                    style={index === 7 && !showAllImages && descriptionImages.length > 8 ? { filter: 'blur(2px)' } : {}}
                  />
                  {index === 7 && !showAllImages && descriptionImages.length > 8 && (
                    <div className="see_more_overlay absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white">
                      <span className="text-[10px] sm:text-[11px] font-medium">See More</span>
                      <span className="text-[8px] sm:text-[9px] mt-0.5">+{descriptionImages.length - 7}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* {product.description && <div className="pd_para" dangerouslySetInnerHTML={createMarkup(product.description)} />} */}

      {/* Product Description Section - conditional button or description */}
      {(() => {
        // Determine which description to show based on source
        // DB products: use img_description (original Alibaba description)
        // Alibaba API / Solr: use description
        const descriptionToShow = product?.description || '';

        // Check if description exists and has meaningful content
        const hasDescription = descriptionToShow && typeof descriptionToShow === 'string';

        if (hasDescription) {
          // Strip HTML tags to check for actual text content
          const textOnly = descriptionToShow
            .replace(/<img[^>]*>/gi, '') // Remove img tags first
            .replace(/<[^>]*>/g, '') // Remove all other HTML tags
            .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
            .replace(/&[a-z]+;/gi, ' ') // Replace other HTML entities
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();

          // Check if there's actual text content (not just empty HTML, images, or placeholders)
          // Must have at least 10 characters of actual text to be meaningful
          const hasContent =
            textOnly.length > 10 &&
            textOnly !== 'null' &&
            !descriptionToShow.includes('offer_template') &&
            !descriptionToShow.includes('offer-template') &&
            !/^[\s\d\W]*$/.test(textOnly); // Not just whitespace, numbers, or special chars

          if (hasContent) {
            // Show the description
            return (
              <div className="mt-4 sm:mt-6">
                <h3 className="product_images_heading text-[15px] sm:text-[16px] font-semibold mb-2 px-1">Product Description</h3>
                <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: prepareDescriptionHtml(descriptionToShow) }} />
              </div>
            );
          }
        }

        // Show "Generate Description" button if no meaningful description
        return (
          <div className="mt-4 sm:mt-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-3"> Click below to view the full product description.</p>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={isGeneratingDescription}
                className="px-4 py-2 bg-[#3DD0E5] text-white rounded-lg hover:bg-[#2bb8cc] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center gap-2 mx-auto"
              >
                {isGeneratingDescription ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      ></path>
                    </svg>
                    Show Description
                  </>
                )}
              </button>
            </div>
          </div>
        );
      })()}

      {/* {product.description && (<div className="pd_para" dangerouslySetInnerHTML={createMarkup(product.description
            .replace(/null/gi, '')      
            .replace(/<p>null<\/p>/gi, '')
            .trim()
            )}
          />
         )} */}

      {zoomModal.show && (
        <div className="zoom_modal" onClick={handleModalClose}>
          <div className="zoom_modal_content" onClick={e => e.stopPropagation()}>
            <button className="modal_close_btn" onClick={handleModalClose}>
              ×
            </button>
            <button className="modal_nav_btn modal_prev" onClick={handlePrevImage}>
              ‹
            </button>
            <img src={zoomModal.image} alt="Zoomed image" className="zoom_modal_image" referrerPolicy="no-referrer" crossOrigin="anonymous" />
            <button className="modal_nav_btn modal_next" onClick={handleNextImage}>
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailRight;
